"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getSoftwareDetailColumns = exports.getSoftwareOverviewColumns = void 0;
const react_1 = __importDefault(require("react"));
const Button_1 = __importDefault(require("../../common/js/atoms/Button"));
const ScopeGuard_1 = __importDefault(require("../../common/js/components/security/ScopeGuard"));
const AuthorizationScopes_1 = __importDefault(require("../../common/js/enums/AuthorizationScopes"));
const ButtonVariant_1 = __importDefault(require("../../common/js/enums/ButtonVariant"));
const Zupicons_1 = __importDefault(require("../../common/js/enums/Zupicons"));
const dateformat_1 = require("../../common/js/helper/dateformat");
const string_1 = require("../../common/js/helper/string");
const table_1 = require("../../common/js/helper/table");
const translation_1 = require("../../common/js/helper/translation");
const AssetsSoftwareColumnHeaderWithTooltip_1 = __importDefault(require("../components/assets/AssetsSoftwareColumnHeaderWithTooltip"));
const AssetsSoftwareContactNameColumn_1 = __importDefault(require("../components/assets/AssetsSoftwareContactNameColumn"));
const AssetsSoftwareLicenseModelColumn_1 = __importDefault(require("../components/assets/AssetsSoftwareLicenseModelColumn"));
const AssetsSoftwareLicenseUsageColumn_1 = __importDefault(require("../components/assets/AssetsSoftwareLicenseUsageColumn"));
const ProductType_1 = __importDefault(require("../enums/ProductType"));
const LicenseStore_1 = __importDefault(require("../stores/LicenseStore"));
const license_1 = require("./license");
const product_1 = require("./product");
// Using callback functions for all columns to ensure everything is translated correctly after language switch.
const NameColumn = (type, showLicenseCount, isSubTable = false) => ({
    allowOverflow: true,
    name: (0, translation_1.t)('myassets.software.table.name.column_name', { type }),
    sortable: true,
    selector: (license) => license.userCount ?? 0,
    format: (license) => (react_1.default.createElement("div", { className: "--white-space-normal my-2 znd-table-tile__name" },
        license.name,
        !!showLicenseCount && (license.userCount ?? 0) > 0 ? (react_1.default.createElement("span", { className: "ml-2" },
            "(",
            (0, translation_1.t)('myassets.software.table.user_count.column_name', {
                count: license.userCount ?? 0,
            }).trim(),
            ")")) : null)),
    sortFunction: sortNameColumn,
    ...(isSubTable ? { minWidth: '22rem', maxWidth: '25%' } : { minWidth: '28.125rem' }),
});
const ActivationDateColumn = (type, isSubTable = false) => ({
    allowOverflow: true,
    name: (0, translation_1.t)('myassets.software.table.install_date.column_name', { type }),
    sortable: true,
    selector: (license) => license.activationDate?.valueOf() ?? 0,
    format: (license) => {
        if (license.activated) {
            return react_1.default.createElement("i", { className: 'znd-asset__check icon-check' });
        }
    },
    ...(isSubTable
        ? {
            maxWidth: '8rem',
            minWidth: '7rem',
        }
        : { minWidth: '8rem' }),
});
const LicenseContactNameColumn = (type, isSubTable = false) => ({
    allowOverflow: true,
    name: (react_1.default.createElement(AssetsSoftwareColumnHeaderWithTooltip_1.default, { title: (0, translation_1.t)('myassets.software.table.licence_contact_name.column_name', { type }), tooltip: (0, translation_1.t)('myassets.software.table.licence_contact_name.tooltip'), iconName: Zupicons_1.default.info })),
    sortable: true,
    selector: (license) => license.licenseContactName ?? '',
    format: (license) => react_1.default.createElement(AssetsSoftwareContactNameColumn_1.default, { license: license }),
    ...(isSubTable ? { maxWidth: '10%' } : { minWidth: '11.25rem' }),
});
const LicenseModelColumn = (type, isSubTable = false) => ({
    allowOverflow: true,
    name: (0, translation_1.t)('myassets.software.table.license_model.column_name', { type }),
    sortable: true,
    selector: (license) => (0, translation_1.t)('myassets.software.table.license_model.enum', {
        model: (0, string_1.slugifyKey)(license.model, true),
        raw: license.model,
    }),
    format: (license) => react_1.default.createElement(AssetsSoftwareLicenseModelColumn_1.default, { license: license }),
    ...(isSubTable
        ? {
            maxWidth: '8%',
            minWidth: '14.5rem',
        }
        : { minWidth: '17.5rem' }),
});
const UsageColumn = (type, isSubTable = false) => ({
    allowOverflow: true,
    name: (react_1.default.createElement(AssetsSoftwareColumnHeaderWithTooltip_1.default, { title: (0, translation_1.t)('myassets.software.table.usage.column_name', { type }), tooltip: (0, translation_1.t)('myassets.software.table.usage.tooltip'), iconName: Zupicons_1.default.info })),
    sortable: true,
    selector: (license) => license.usage ?? '',
    format: (license) => react_1.default.createElement(AssetsSoftwareLicenseUsageColumn_1.default, { license: license }),
    ...(isSubTable
        ? {
            maxWidth: '10%',
            minWidth: '10rem',
        }
        : { minWidth: '17.5rem' }),
});
const LicenseFileColumn = (type, isSubTable = false) => ({
    allowOverflow: true,
    name: (0, translation_1.t)('myassets.software.table.license_file_name.column_name', { type }),
    sortable: true,
    selector: (license) => license.fileName ?? '',
    format: (license) => license.fileName ? (react_1.default.createElement(ScopeGuard_1.default, { fallback: react_1.default.createElement("div", { className: "--white-space-normal my-2" }, license.fileName), scopes: [AuthorizationScopes_1.default.LICENSE_DOWNLOAD] },
        react_1.default.createElement(Button_1.default, { className: "znd-assets-table-sub__filename", variant: ButtonVariant_1.default.text, iconRight: Zupicons_1.default.angleRight, text: license.fileName, disabled: LicenseStore_1.default.isDownloading, onClick: () => {
                (0, product_1.prepareDownloadLicenses)(license);
            } }))) : null,
    ...(isSubTable ? { minWidth: '11rem' } : { minWidth: '11rem' }),
});
const EndDateColumn = (type, isSubTable = false) => ({
    allowOverflow: true,
    name: (0, translation_1.t)('myassets.software.table.end_of_subscription.column_name', { type }),
    sortable: true,
    selector: (license) => license.endDate?.valueOf() ?? 0,
    format: (license) => (0, dateformat_1.getDateFormat)(license.endDate) ?? '',
    ...(isSubTable
        ? {
            maxWidth: '15%',
            minWidth: '11.5rem',
        }
        : { minWidth: '9.5rem' }),
});
const ExternalIdColumn = (type, isSubTable = false) => ({
    allowOverflow: true,
    name: (0, translation_1.t)('myassets.software.table.license_id.column_name', { type }),
    sortable: true,
    selector: (license) => license.externalId ?? '',
    format: (license) => (react_1.default.createElement("div", { className: "--white-space-normal my-2" }, ellipsisColumn(license.externalId, 5, license, (0, string_1.slugifyKey)(license.productType) === ProductType_1.default.PRIMECENTER))),
    ...(isSubTable
        ? {
            maxWidth: '10%',
            minWidth: '20rem',
        }
        : { minWidth: '22rem' }),
});
const ContainerNumberColumn = (type, isSubTable = false) => ({
    allowOverflow: true,
    name: (0, translation_1.t)('myassets.software.table.container_number.column_name', { type }),
    selector: (license) => license.containerNumber ?? '',
    sortable: true,
    ...(isSubTable
        ? {
            maxWidth: '10%',
            minWidth: '10.5rem',
        }
        : { minWidth: '9rem' }),
});
const ClientNameColumn = (type, isSubTable = false) => ({
    allowOverflow: true,
    name: (0, translation_1.t)('myassets.software.table.client_name.column_name', { type }),
    selector: (license) => license.clientName ?? '',
    sortable: true,
    ...(isSubTable ? { minWidth: '10rem' } : { minWidth: '10rem' }),
});
const MachineExternalIdColumn = (type, isSubTable = false) => ({
    allowOverflow: true,
    name: (0, translation_1.t)('myassets.software.table.machine_external_id.column_name', { type }),
    selector: (license) => license.massiveMachine?.externalId ?? '',
    sortable: true,
    ...(isSubTable
        ? {
            maxWidth: '10%',
            minWidth: '11rem',
        }
        : { minWidth: '7.75rem' }),
});
const ActivationCodeColumn = (type, isSubTable = false) => ({
    allowOverflow: true,
    name: (0, translation_1.t)('myassets.software.table.activation_code.column_name', { type }),
    sortable: true,
    selector: (license) => license.activationCode ?? '',
    format: (license) => (react_1.default.createElement("div", { className: "--white-space-normal my-2" }, ellipsisColumn(license?.activationCode ?? '', 5, license))),
    ...(isSubTable
        ? {
            maxWidth: '10%',
            minWidth: '15.5rem',
        }
        : {
            maxWidth: '10%',
            minWidth: '15.5rem',
        }),
});
const defaultColumnsCallback = () => [
    NameColumn(),
    LicenseModelColumn(),
    UsageColumn(),
    ActivationDateColumn(),
    LicenseContactNameColumn(),
    EndDateColumn(),
    ExternalIdColumn(),
];
// noinspection DuplicatedCode
const TypeColumnsCallbackMap = {
    [ProductType_1.default.ZUND_DESIGN_CENTER]: ({ type, isSubTable }) => [
        NameColumn(type, true, isSubTable),
        ActivationDateColumn(type, isSubTable),
        EndDateColumn(type, isSubTable),
        ExternalIdColumn(type, isSubTable),
        ContainerNumberColumn(type, isSubTable),
        ClientNameColumn(type, isSubTable),
    ],
    [ProductType_1.default.ZUND_PRECUT_CENTER]: ({ type, isSubTable }) => [
        NameColumn(type, true, isSubTable),
        ActivationDateColumn(type, isSubTable),
        EndDateColumn(type, isSubTable),
        ExternalIdColumn(type, isSubTable),
        ContainerNumberColumn(type, isSubTable),
        ClientNameColumn(type, isSubTable),
    ],
    [ProductType_1.default.MINDCUT]: ({ type, lockType, isSubTable, }) => (0, license_1.isSoftLock)(lockType)
        ? [
            NameColumn(type, true, isSubTable),
            ActivationDateColumn(type, isSubTable),
            EndDateColumn(type, isSubTable),
            ExternalIdColumn(type, isSubTable),
            MachineExternalIdColumn(type, isSubTable),
        ]
        : [
            NameColumn(type, true, isSubTable),
            ActivationDateColumn(type, isSubTable),
            EndDateColumn(type, isSubTable),
            ExternalIdColumn(type, isSubTable),
            LicenseFileColumn(type, isSubTable),
            MachineExternalIdColumn(type, isSubTable),
        ],
    [ProductType_1.default.MINDCAD]: ({ type, lockType, isSubTable, }) => (0, license_1.isSoftLock)(lockType)
        ? [
            NameColumn(type, true, isSubTable),
            ActivationDateColumn(type, isSubTable),
            EndDateColumn(type, isSubTable),
            ExternalIdColumn(type, isSubTable),
            MachineExternalIdColumn(type, isSubTable),
        ]
        : [
            NameColumn(type, true, isSubTable),
            ActivationDateColumn(type, isSubTable),
            EndDateColumn(type, isSubTable),
            ExternalIdColumn(type, isSubTable),
            LicenseFileColumn(type, isSubTable),
            MachineExternalIdColumn(type, isSubTable),
        ],
    [ProductType_1.default.MINDGEST]: ({ type, lockType, isSubTable, }) => (0, license_1.isSoftLock)(lockType)
        ? [
            NameColumn(type, true, isSubTable),
            ActivationDateColumn(type, isSubTable),
            EndDateColumn(type, isSubTable),
            ExternalIdColumn(type, isSubTable),
            MachineExternalIdColumn(type, isSubTable),
        ]
        : [
            NameColumn(type, true, isSubTable),
            ActivationDateColumn(type, isSubTable),
            EndDateColumn(type, isSubTable),
            ExternalIdColumn(type, isSubTable),
            LicenseFileColumn(type, isSubTable),
            MachineExternalIdColumn(type, isSubTable),
        ],
    [ProductType_1.default.MINDGUEST]: ({ type, lockType, isSubTable, }) => (0, license_1.isSoftLock)(lockType)
        ? [
            NameColumn(type, true, isSubTable),
            ActivationDateColumn(type, isSubTable),
            EndDateColumn(type, isSubTable),
            ExternalIdColumn(type, isSubTable),
            MachineExternalIdColumn(type, isSubTable),
        ]
        : [
            NameColumn(type, true, isSubTable),
            ActivationDateColumn(type, isSubTable),
            EndDateColumn(type, isSubTable),
            ExternalIdColumn(type, isSubTable),
            LicenseFileColumn(type, isSubTable),
            MachineExternalIdColumn(type, isSubTable),
        ],
    [ProductType_1.default.ZUND_CUT_CENTER]: ({ type, isSubTable }) => [
        NameColumn(type, true, isSubTable),
        ActivationDateColumn(type, isSubTable),
        EndDateColumn(type, isSubTable),
        ExternalIdColumn(type, isSubTable),
        ContainerNumberColumn(type, isSubTable),
        ClientNameColumn(type, isSubTable),
        MachineExternalIdColumn(type, isSubTable),
    ],
    [ProductType_1.default.PRIMECENTER]: ({ type, isSubTable }) => [
        NameColumn(type, true, isSubTable),
        ActivationDateColumn(type, isSubTable),
        EndDateColumn(type, isSubTable),
        ExternalIdColumn(type, isSubTable),
        ActivationCodeColumn(type, isSubTable),
    ],
    [ProductType_1.default.ZUND_CONNECT]: ({ type, isSubTable }) => [
        NameColumn(type, true, isSubTable),
        ActivationDateColumn(type, isSubTable),
        EndDateColumn(type, isSubTable),
        ExternalIdColumn(type, isSubTable),
        MachineExternalIdColumn(type, isSubTable),
    ],
};
const getSoftwareOverviewColumns = () => defaultColumnsCallback();
exports.getSoftwareOverviewColumns = getSoftwareOverviewColumns;
const getSoftwareDetailColumns = (type, lockType) => {
    const slug = (0, string_1.slugifyKey)(type);
    const columns = TypeColumnsCallbackMap[slug];
    return !!columns ? columns({ type: slug, lockType, isSubTable: true }) : defaultColumnsCallback();
};
exports.getSoftwareDetailColumns = getSoftwareDetailColumns;
const ellipsisColumn = (text, limit, license, noEllipsis) => {
    if (noEllipsis) {
        return text;
    }
    const mustBeShortened = (0, table_1.mustTextBeShortened)(license);
    if (mustBeShortened) {
        return (0, string_1.ellipsis)(text, limit);
    }
    return text;
};
const sortNameColumn = (a, b) => {
    if (!a.name) {
        return -1;
    }
    if (!b.name) {
        return 1;
    }
    if (0 === a.name.localeCompare(b.name)) {
        if (!a.activationDate) {
            return -1;
        }
        if (!b.activationDate) {
            return 1;
        }
        return a.activationDate > b.activationDate ? -1 : 1;
    }
    return a.name.localeCompare(b.name);
};
